import { useEffect, useState } from 'react';
import { Award, CalendarCheck, ClipboardCheck, MessageCircle, Sparkles, Wallet } from 'lucide-react';
import { Button, ModalOverlay } from './ui';
import { clinicWelcomeIsPending, dismissClinicWelcome } from '../lib/clinicWelcome';

const STEPS = [
  {
    eyebrow: 'Welcome',
    title: (name: string) => (name ? `Welcome, ${name}` : 'Welcome to PhysioEDGE'),
    lead: 'Your clinic is live. Students book internships on the schedules PhysioEDGE publishes for you — this home page is where that work lands.',
    Icon: Sparkles,
    hints: [
      { Icon: CalendarCheck, text: 'New bookings appear here as soon as a student confirms a week.' },
      { Icon: ClipboardCheck, text: 'Tap Active interns, This week, pending ratings, or unread messages to see who and which branch.' },
    ],
  },
  {
    eyebrow: 'Internships',
    title: () => 'Run each student week',
    lead: 'When a booking shows up, work it in this order. The right action appears on the booking when it is time.',
    Icon: ClipboardCheck,
    hints: [
      { Icon: CalendarCheck, text: 'Attendance — mark training session days as students attend.' },
      { Icon: ClipboardCheck, text: 'Complete — close out the week after student session hours finish.' },
      { Icon: Sparkles, text: 'Rate — score 4 criteria: Professionalism, Ethical Code, Time Respect, Communication.' },
      { Icon: Award, text: 'Certificate — generate and issue official completion certificates.' },
    ],
  },
  {
    eyebrow: 'Earnings & questions',
    title: () => 'Wallet & Student Messages',
    lead: 'Track wallet balance, request earnings payouts, and answer student questions directly from the WhatsApp-style right chat panel.',
    Icon: Wallet,
    hints: [
      { Icon: Wallet, text: 'Request withdrawals anytime your available wallet balance is above 0 EGP.' },
      { Icon: MessageCircle, text: 'Student mentor questions appear in the right chat panel — answer before deadlines.' },
      { Icon: CalendarCheck, text: 'Access Account from the left sidebar for clinic details and staff account management.' },
    ],
  },
] as const;

export function ClinicWelcomeTour({
  userId = '',
  firstName = '',
  onDone,
}: {
  userId?: string;
  firstName?: string;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(() => Boolean(userId && clinicWelcomeIsPending(userId)));
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState<'forward' | 'back'>('forward');

  useEffect(() => {
    if (userId && clinicWelcomeIsPending(userId)) setOpen(true);
  }, [userId]);

  if (!open) return null;

  const handleFinish = () => {
    dismissClinicWelcome(userId);
    setOpen(false);
    if (onDone) onDone();
  };

  const last = step === STEPS.length - 1;
  const page = STEPS[step] ?? STEPS[0]!;
  const PageIcon = page.Icon;
  const title = page.title(firstName.trim());

  const go = (next: number) => {
    if (next < 0 || next >= STEPS.length || next === step) return;
    setDir(next > step ? 'forward' : 'back');
    setStep(next);
  };

  return (
    <ModalOverlay>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="clinic-welcome-title"
        aria-describedby="clinic-welcome-lead"
        className="app-dialog bg-surface shadow-pop w-full max-w-lg p-5 sm:p-8"
      >
        <div className="flex items-center justify-between gap-3 mb-4 sm:mb-5 shrink-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-faint">
            {page.eyebrow}
          </p>
          <p className="text-[11px] font-semibold tabular-nums text-ink-faint" aria-live="polite">
            {step + 1} of {STEPS.length}
          </p>
        </div>

        <div
          key={step}
          className={`page-enter page-enter--${dir} flex-1 min-h-0 overflow-y-auto overscroll-contain`}
        >
          <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-sand-soft text-sand-strong flex items-center justify-center mb-4 sm:mb-5">
            <PageIcon className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={1.75} aria-hidden />
          </div>
          <h1 id="clinic-welcome-title" className="font-heading text-xl sm:text-2xl font-bold text-ink mb-2">
            {title}
          </h1>
          <p id="clinic-welcome-lead" className="text-sm text-ink-soft leading-relaxed mb-4 sm:mb-5">
            {page.lead}
          </p>
          <ul className="space-y-3">
            {page.hints.map(hint => (
              <li key={hint.text} className="flex gap-3">
                <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-teal-soft text-teal flex items-center justify-center shrink-0">
                  <hint.Icon className="w-4 h-4" aria-hidden />
                </span>
                <span className="text-sm text-ink leading-relaxed pt-1">{hint.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center justify-center gap-2 mt-5 mb-4 shrink-0" role="tablist" aria-label="Guide pages">
          {STEPS.map((s, i) => (
            <button
              key={s.eyebrow}
              type="button"
              role="tab"
              aria-selected={i === step}
              aria-label={`Page ${i + 1}: ${s.eyebrow}`}
              onClick={() => go(i)}
              className={`h-2 rounded-full transition-all ${
                i === step ? 'w-7 bg-sand' : 'w-2 bg-line hover:bg-ink-faint'
              }`}
            />
          ))}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 shrink-0">
          <Button
            type="button"
            variant="ghost"
            className="w-full sm:w-auto sm:min-w-[7.5rem]"
            onClick={() => go(step - 1)}
            disabled={step === 0}
          >
            Previous
          </Button>
          <Button
            type="button"
            className="w-full sm:flex-1"
            size="lg"
            onClick={() => (last ? handleFinish() : go(step + 1))}
          >
            {last ? 'Got it' : 'Next'}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
